/**
 * Shape rows for the CSV exports (T1.10) exactly in EXPORT_COLUMNS order. Pure.
 */
import {
  EXPORT_COLUMNS,
  toCsv,
  type InterventionRow,
  type SpeciesRow,
  type SurveyRow,
  type SurveySummaryRow,
  type TreeLatestRow,
  type TreeRow,
  type ZoneRow,
} from '@treetrack/protocol';
import { diameterAt, girthAt, type MeasurementWithGirths } from './aggregate';

export interface ExportBundle {
  trees: readonly TreeRow[];
  latest: ReadonlyMap<string, TreeLatestRow>;
  zones: readonly ZoneRow[];
  species: readonly SpeciesRow[];
  surveys: readonly SurveyRow[];
  summaries: readonly SurveySummaryRow[];
  measurements: readonly MeasurementWithGirths[];
  interventions: readonly InterventionRow[];
}

type Row<K extends keyof typeof EXPORT_COLUMNS> = Record<(typeof EXPORT_COLUMNS)[K][number], unknown>;

function lookups(b: ExportBundle) {
  return {
    zoneLabel: new Map(b.zones.map((z) => [z.id, z.label])),
    species: new Map(b.species.map((s) => [s.id, s])),
    treeById: new Map(b.trees.map((t) => [t.id, t])),
    surveyById: new Map(b.surveys.map((s) => [s.id, s])),
  };
}

export function treeExportRows(b: ExportBundle): Row<'trees'>[] {
  const L = lookups(b);
  return b.trees
    .slice()
    .sort((x, y) => x.tag.localeCompare(y.tag))
    .map((t) => {
      const l = b.latest.get(t.id);
      const sp = t.species_id ? L.species.get(t.species_id) : undefined;
      return {
        tag: t.tag,
        zone_label: t.zone_id ? (L.zoneLabel.get(t.zone_id) ?? '') : '',
        species_en: sp?.name_en ?? '',
        species_es: sp?.name_es ?? '',
        status: t.status,
        lat: t.lat,
        lng: t.lng,
        gps_accuracy_m: t.gps_accuracy_m,
        grid_row: t.grid_row,
        grid_col: t.grid_col,
        planted_on: t.planted_on,
        last_survey_date: l?.survey_date ?? null,
        last_condition: l?.condition ?? null,
        last_height_cm: l?.height_cm ?? null,
        last_girth_basal_cm: l?.girth_basal_cm ?? null,
        last_diameter_basal_cm: l?.diameter_basal_cm ?? null,
        last_girth_dbh_cm: l?.girth_dbh_cm ?? null,
        last_diameter_dbh_cm: l?.diameter_dbh_cm ?? null,
        tree_id: t.id,
      };
    });
}

export function measurementExportRows(b: ExportBundle): Row<'measurements'>[] {
  const L = lookups(b);
  return b.measurements
    .map((m) => {
      const tree = L.treeById.get(m.tree_id);
      const survey = L.surveyById.get(m.survey_id);
      const sp = tree?.species_id ? L.species.get(tree.species_id) : undefined;
      return {
        survey_date: survey?.date ?? m.recorded_at.slice(0, 10),
        tag: tree?.tag ?? '',
        zone_label: tree?.zone_id ? (L.zoneLabel.get(tree.zone_id) ?? '') : '',
        species_en: sp?.name_en ?? '',
        condition: m.condition,
        height_cm: m.height_cm,
        height_method: m.height_method,
        girth_basal_cm: girthAt(m, 10),
        diameter_basal_cm: diameterAt(m, 10),
        girth_dbh_cm: girthAt(m, 130),
        diameter_dbh_cm: diameterAt(m, 130),
        soil_moisture: m.soil_moisture,
        soil_temp: m.soil_temp,
        chips: m.chips,
        note: m.note,
        team_name: survey?.team_name ?? null,
        device_lat: m.device_lat,
        device_lng: m.device_lng,
        device_accuracy_m: m.device_accuracy_m,
        recorded_at: m.recorded_at,
        measurement_id: m.id,
        tree_id: m.tree_id,
        survey_id: m.survey_id,
      };
    })
    .sort((x, y) => String(x.survey_date).localeCompare(String(y.survey_date)) || String(x.tag).localeCompare(String(y.tag)));
}

export function surveyExportRows(b: ExportBundle): Row<'surveys'>[] {
  const L = lookups(b);
  const summary = new Map(b.summaries.map((s) => [s.survey_id, s]));
  return b.surveys
    .slice()
    .sort((x, y) => y.date.localeCompare(x.date))
    .map((s) => {
      const sum = summary.get(s.id);
      return {
        date: s.date,
        zone_label: s.zone_id ? (L.zoneLabel.get(s.zone_id) ?? '') : '',
        team_name: s.team_name,
        members: s.members,
        weather: s.weather,
        found: sum?.found ?? null,
        alive: sum?.alive ?? null,
        not_found: sum?.not_found ?? null,
        survival_rate_pct: sum?.survival_rate_pct ?? null,
        care_plan: s.care_plan,
        mulch_plan: s.mulch_plan,
        pest_notes: s.pest_notes,
        irrigation_bottles: s.irrigation_bottles,
        notes: s.notes,
        survey_id: s.id,
      };
    });
}

export function interventionExportRows(b: ExportBundle): Row<'interventions'>[] {
  const L = lookups(b);
  return b.interventions
    .slice()
    .sort((x, y) => y.done_at.localeCompare(x.done_at))
    .map((i) => {
      const tree = i.tree_id ? L.treeById.get(i.tree_id) : undefined;
      const zoneId = i.zone_id ?? tree?.zone_id ?? null;
      const survey = i.survey_id ? L.surveyById.get(i.survey_id) : undefined;
      return {
        done_at: i.done_at,
        type: i.type,
        quantity: i.quantity,
        unit: i.unit,
        tag: tree?.tag ?? '',
        zone_label: zoneId ? (L.zoneLabel.get(zoneId) ?? '') : '',
        team_name: survey?.team_name ?? null,
        note: i.note,
        intervention_id: i.id,
      };
    });
}

export function exportCsv(kind: keyof typeof EXPORT_COLUMNS, b: ExportBundle): string {
  switch (kind) {
    case 'trees':
      return toCsv(treeExportRows(b), EXPORT_COLUMNS.trees);
    case 'measurements':
      return toCsv(measurementExportRows(b), EXPORT_COLUMNS.measurements);
    case 'surveys':
      return toCsv(surveyExportRows(b), EXPORT_COLUMNS.surveys);
    case 'interventions':
      return toCsv(interventionExportRows(b), EXPORT_COLUMNS.interventions);
  }
}

export function exportFilename(projectCode: string, kind: string, ext: string, today: string): string {
  return `${projectCode}-${kind}-${today}.${ext}`;
}
